import { classifyInsightActionError } from '../service/classifyInsightActionError'
import type { InsightActionStore } from '../stores/insightActionStore'

import type { InsightActionApiSlice, RunScope } from './insightActionFlow.types'

export const itemToken = (itemId: number): string => `item:${itemId}`
export const runToken = (runId: string): string => `run:${runId}`
export const cancelToken = (requestId: string): string => `cancel:${requestId}`

export interface RevertFlowDeps {
  api: InsightActionApiSlice<'revertItem' | 'revertRun'>
  store: InsightActionStore
}

export class RevertFlow {
  constructor(private readonly deps: RevertFlowDeps) {}

  async item(scope: RunScope, runId: string, itemId: number): Promise<boolean> {
    const accepted = await this.guarded(itemToken(itemId), async () => {
      await this.deps.api.revertItem(scope.accountId, scope.workspaceId, runId, itemId)
      return true
    })
    return accepted === true
  }

  async run(scope: RunScope, runId: string): Promise<number> {
    const queued = await this.guarded(runToken(runId), async () => {
      const response = await this.deps.api.revertRun(scope.accountId, scope.workspaceId, runId)
      return response.items_queued ?? 0
    })
    if (queued !== null) this.deps.store.setRevertQueued(runId, queued)
    return queued ?? 0
  }

  private async guarded<T>(token: string, call: () => Promise<T>): Promise<T | null> {
    this.deps.store.setReverting(token, true)
    try {
      return await call()
    } catch (cause) {
      this.deps.store.setRevertError(token, classifyInsightActionError(cause).code)
      return null
    } finally {
      this.deps.store.setReverting(token, false)
    }
  }
}
